import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, Disc3 } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const LOGO = 'https://customer-assets.emergentagent.com/job_radio-jukebox-2/artifacts/u6n2aq57_LOGO%20MAJESTIC%20JUKEBOX%20RADIO%20OK.jpg';

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/', label: 'Home', testId: NAV.homeLink },
    { to: '/about', label: 'About', testId: NAV.aboutLink },
    { to: '/contact', label: 'Contact', testId: NAV.contactLink },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-vinyl/85 border-b border-amber/15 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO}
            alt="Majestic Jukebox Radio"
            className="w-10 h-10 rounded-full object-cover ring-2 ring-amber/40 group-hover:ring-amber transition"
          />
          <div className="hidden sm:block">
            <div className="font-display text-lg leading-none text-cream tracking-wide">Majestic Jukebox</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mt-1">Radio · Since Forever</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === '/'}
              className={({ isActive }) =>
                `relative font-body text-sm uppercase tracking-[0.18em] transition-colors ${
                  isActive ? 'text-amber' : 'text-cream/80 hover:text-amber'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-2 left-1/2 -translate-x-1/2 h-px transition-all duration-300 ${
                    isActive ? 'w-6 bg-amber' : 'w-0 bg-transparent'
                  }`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/30 bg-red-500/5"
          data-testid={NAV.listenLiveBadge}>
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-red-300 font-bold">On Air 24/7</span>
        </div>

        <button
          data-testid={NAV.mobileMenuToggle}
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-cream"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-amber/15 bg-vinyl/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                end={l.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-body text-base uppercase tracking-[0.18em] ${
                    isActive ? 'text-amber' : 'text-cream/80'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
